/**
 * Filter Intake Template Investment Types
 *
 * Utility to filter investment type options in intake templates based on
 * business configuration. Business admins can configure which loan products
 * appear on their intake forms.
 *
 * @see Issue #526 - Configurable Intake Form Investment Types
 */

import type { IntakeFlowTemplate, IntakeInvestmentTypeConfig } from '@ops-matrix/shared-types';

const PARTNER_ONLY_INVESTMENT_TYPES: ReadonlySet<string> = new Set(['partner_invitation', 'partner_onboarding']);

/**
 * Filter the investment type options in a template based on business configuration
 *
 * @param template - The intake flow template to filter
 * @param config - The business's investment type configuration
 * @returns A new template with filtered investment type options
 */
export function filterTemplateInvestmentTypes(
  template: IntakeFlowTemplate,
  config: IntakeInvestmentTypeConfig | null | undefined,
): IntakeFlowTemplate {
  // If no config provided, return template as-is (all options shown)
  if (!config) {
    return template;
  }

  // Deep clone the template to avoid mutation
  const filteredTemplate = structuredClone(template);

  // Find the investment type field in all steps
  for (const step of filteredTemplate.data.steps) {
    for (const field of step.fields) {
      // Check if this is the investment type field
      if (field.name === 'investmentType' && field.options) {
        // Filter options based on supported types
        field.options = field.options.filter((option) => {
          const value = option.value;

          // Always filter out partner-specific types (not consumer-facing)
          if (PARTNER_ONLY_INVESTMENT_TYPES.has(value)) {
            return false;
          }

          // Handle "general" (Not Sure) option
          if (value === 'general') {
            return config.alwaysShowGeneral;
          }

          // Check if this investment type is supported
          return config.supportedInvestmentTypes.includes(value as 'bridge' | 'construction' | 'rental' | 'commercial');
        });

        // If we found and filtered the investment type field in this step, move to next step
        break;
      }
    }
  }

  return filteredTemplate;
}

/**
 * Check if a given investment type is supported by the business
 *
 * @param investmentType - The investment type to check
 * @param config - The business's investment type configuration
 * @returns true if the type is supported
 */
export function isInvestmentTypeSupported(
  investmentType: string,
  config: IntakeInvestmentTypeConfig | null | undefined,
): boolean {
  // If no config, all types are supported by default
  if (!config) {
    return true;
  }

  // Handle "general" (Not Sure) option
  if (investmentType === 'general') {
    return config.alwaysShowGeneral;
  }

  // Partner-specific types are not consumer-facing
  if (PARTNER_ONLY_INVESTMENT_TYPES.has(investmentType)) {
    return false;
  }

  // Check if the investment type is in the supported list
  return config.supportedInvestmentTypes.includes(
    investmentType as 'bridge' | 'construction' | 'rental' | 'commercial',
  );
}
